import React, { useEffect, useMemo, useState } from 'react'

import { useRouter } from 'next/router'

import { RoutesEnum } from 'src/common/enums/routesEnum'
import { MainLayout } from 'src/common/layouts/MainLayout/MainLayout'
import { ArticleClientType } from 'src/strapi/types/articleTypes'
import { TagClientType } from 'src/strapi/types/tagTypes'
import { AnalyticEventEnum } from 'src/utils/analytic/analyticEventEnum'
import { useAnalytic } from 'src/utils/analytic/useAnalytic'

import { BlogCardsBlock } from './BlogCardsBlock/BlogCardsBlock'
import { BlogGetMatchedBlock } from './BlogGetMatchedBlock/BlogGetMatchedBlock'
import { BlogMeta } from './BlogMeta/BlogMeta'
import { BlogTagsBlock } from './BlogTagsBlock/BlogTagsBlock'

export type BlogPageProps = {
  tags: Array<TagClientType>
  articles: Array<ArticleClientType>
}

export const BlogPage: React.FC<BlogPageProps> = ({ tags, articles }) => {
  const {
    shownArticles,
    selectedTagsNames,
    hasNextPage,
    handleSelectTag,
    handleUnselectTag,
    handleLoadMore,
  } = useBlogPage(articles)

  const { analytic } = useAnalytic()
  analytic.event(AnalyticEventEnum.BLOG_PAGE)

  return (
    <>
      <BlogMeta />

      <MainLayout pageAnalytic={'blog'}>
        <BlogTagsBlock
          tags={tags}
          selectedTagsNames={selectedTagsNames}
          onSelectTag={handleSelectTag}
          onUnselectTag={handleUnselectTag}
        />
        <BlogCardsBlock
          articles={shownArticles}
          hasNextPage={hasNextPage}
          onLoadMore={handleLoadMore}
        />

        <BlogGetMatchedBlock />
      </MainLayout>
    </>
  )
}

function useBlogPage(articles: Array<ArticleClientType>) {
  const router = useRouter()

  const [page, setPage] = useState(1)
  const [selectedTagsNames, setSelectedTagsNames] = useState<Array<string>>([])

  const filteredArticles = useMemo(() => {
    const filteredByTags = articles.filter(({ tags }) => {
      if (!selectedTagsNames.length) {
        return true
      }

      return tags
        .map(({ text }) => text)
        .find((tagName) => selectedTagsNames.includes(tagName))
    })

    return filteredByTags
  }, [articles, selectedTagsNames])

  const shownArticles = filteredArticles.slice(0, page * 6)
  const hasNextPage = shownArticles.length < filteredArticles.length

  const pushTagsToQuery = (tags: Array<string>) => {
    router.push(
      {
        pathname: RoutesEnum.BLOG_PAGE,
        query: tags.length ? { tags: tags.join(',') } : {},
      },
      undefined,
      { shallow: true }
    )
  }

  const handleSelectTag = (tagName: string) => {
    setPage(1)
    setSelectedTagsNames((prev) => {
      const newTags = [...prev, tagName]
      pushTagsToQuery(newTags)

      return newTags
    })
  }

  const handleUnselectTag = (tagName: string) => {
    setPage(1)
    setSelectedTagsNames((prev) => {
      const newTags = prev.filter((text) => text !== tagName)
      pushTagsToQuery(newTags)

      return newTags
    })
  }

  const handleLoadMore = () => {
    setPage((page) => page + 1)
  }

  useEffect(() => {
    if (router.isReady) {
      const query = router.query
      const tags = (query?.tags as string | undefined)?.split(',')

      if (tags) {
        setSelectedTagsNames(tags)
      }
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.isReady])

  return {
    shownArticles,
    hasNextPage,
    selectedTagsNames,
    handleSelectTag,
    handleUnselectTag,
    handleLoadMore,
  }
}
